// The independent lexical WITNESS that corroborates a tree-sitter `ERROR` before
// a marker is emitted (CK-C adopted policy). A parser rejection alone is NOT
// treated as truth — a witness is one of:
//   1. a definite STRUCTURAL contradiction (unmatched paren; infix operator or
//      logical connective with no operand before a connective / `,` / `)`;
//      a trailing operator/connective on an explicitly terminated statement);
//   2. a STRUCTURAL-KEYWORD TYPO: an unquoted word at Damerau-Levenshtein
//      distance exactly 1 from the only structural keyword allowed in a tightly
//      defined slot (head SELECT/UPDATE/INSERT/DELETE; SELECT→FROM; UPDATE→SET;
//      INSERT→VALUES; DELETE→FROM), sitting on / next to the ERROR span. When
//      the GENUINE keyword is present at depth 0 the slot is considered filled
//      and no typo is hunted (a valid column named `form` never witnesses).
// Grammar-too-strict rejections elsewhere in otherwise legal SQL stay
// suppressed — they satisfy no witness.

import type { SqlEngine, SqlRange, StatementKind } from '@/features/sql-grammar/model';

import { buildKeywordTypoMessage } from './keywordTypoMessage';
import { isDamerauDistanceOne, neutralize, tokenize, type SyntaxToken } from './treeSitterSyntaxLex';

/** The witness needs only offsets — callers may pass full `SqlRange`s. */
export type WitnessRange = Pick<SqlRange, 'startOffset' | 'endOffset'>;

export type SyntaxWitness = {
  /** Absolute source offsets of the witness token (the range to squiggle). */
  readonly start: number;
  readonly end: number;
  readonly message: string;
};

const HEAD_KEYWORDS = ['SELECT', 'UPDATE', 'INSERT', 'DELETE'];
// `*` is deliberately ABSENT (SELECT *, COUNT(*)); `:=`/`=>`/`(+)` tokenize as
// distinct shapes and never match these.
const INFIX_OPS = new Set(['=', '<', '>', '<=', '>=', '<>', '!=', '||', '+', '-', '/', '%']);
const CONNECTIVES = new Set(['AND', 'OR']);
const DANGLING_NEXT_PUNCT = new Set([',', ')', ';']);

/**
 * Find a witness for statement `S` given the overlapping confidence-1.0 error
 * ranges. `statementStart` is the absolute offset of `statementText[0]`;
 * `explicitlyTerminated` = the source carries a statement terminator after this
 * statement (the scanner strips it from `text`). Returns the absolute witness
 * token range + message, or `null` (⇒ suppress).
 */
export function findSyntaxWitness(params: {
  readonly engine: SqlEngine;
  readonly kind: StatementKind;
  readonly statementText: string;
  readonly statementStart: number;
  readonly explicitlyTerminated: boolean;
  readonly errorRanges: readonly WitnessRange[];
}): SyntaxWitness | null {
  const { engine, kind, statementText, statementStart, explicitlyTerminated, errorRanges } = params;
  const { code } = neutralize(statementText, engine);
  const toks = tokenize(code);
  if (toks.length === 0) return null;

  const typo = findTypoWitness(kind, toks, statementText, statementStart, code, errorRanges);
  if (typo) return typo;

  return findStructuralWitness(toks, statementStart, explicitlyTerminated);
}

// ── Structural-keyword typo ────────────────────────────────────────────────

function findTypoWitness(
  kind: StatementKind,
  toks: readonly SyntaxToken[],
  statementText: string,
  statementStart: number,
  code: string,
  errorRanges: readonly WitnessRange[],
): SyntaxWitness | null {
  const candidate =
    headTypoSlot(toks) ??
    (kind === 'select' ? fromSlotAfterProjection(toks) : null) ??
    (kind === 'update' ? setSlot(toks) : null) ??
    (kind === 'insert' ? valuesSlot(toks) : null) ??
    (kind === 'delete' ? deleteFromSlot(toks) : null);
  if (!candidate) return null;
  const { token, keyword } = candidate;
  if (!tokenNearError(token, code, statementStart, errorRanges)) return null;
  const raw = statementText.slice(token.start, token.end);
  return {
    start: statementStart + token.start,
    end: statementStart + token.end,
    // Built through the shared contract — the `fix-keyword-typo` quick fix
    // parses this exact message back to recover `raw` + `keyword`.
    message: buildKeywordTypoMessage(raw, keyword),
  };
}

type Candidate = { readonly token: SyntaxToken; readonly keyword: string };

/** A slot-keyword TYPO: Damerau-distance-1 from `keyword` but NOT a strict
 *  prefix of it. A strict prefix (`SELEC`, `FRO`, `VALUE`, …) is the normal
 *  mid-typing state of the finished keyword — flagging it would squiggle every
 *  in-progress `SELECT`/`FROM`/`VALUES` after the 400 ms idle pause (F1). The
 *  zero-false-positive invariant wins, so prefixes stay suppressed even on an
 *  explicitly terminated statement; genuine misspellings (`SELCT`, `FORM`,
 *  `STE`, `VAULES`) are not prefixes and remain detected. */
function isKeywordTypo(text: string, keyword: string): boolean {
  if (isStrictPrefixOf(text, keyword)) return false;
  return isDamerauDistanceOne(text, keyword);
}

function isStrictPrefixOf(text: string, keyword: string): boolean {
  return text.length < keyword.length && keyword.toUpperCase().startsWith(text.toUpperCase());
}

function headTypoSlot(toks: readonly SyntaxToken[]): Candidate | null {
  const first = toks[0];
  if (!first || first.type !== 'word') return null;
  for (const kw of HEAD_KEYWORDS) {
    if (isKeywordTypo(first.text, kw)) return { token: first, keyword: kw };
  }
  return null;
}

/** Depth-0 word scan after the head. The slot only yields a candidate when the
 *  GENUINE keyword appears NOWHERE at depth 0 (present ⇒ the slot is filled and
 *  the parse error is elsewhere — a column named `form` must never witness). */
function scanSlot(
  toks: readonly SyntaxToken[],
  keyword: string,
  accept: (idx: number) => boolean,
): Candidate | null {
  let depth = 0;
  let candidate: Candidate | null = null;
  for (let idx = 1; idx < toks.length; idx += 1) {
    const t = toks[idx];
    if (t.type === 'punct' && t.text === '(') { depth += 1; continue; }
    if (t.type === 'punct' && t.text === ')') { depth -= 1; continue; }
    if (depth !== 0 || t.type !== 'word') continue;
    if (t.text.toUpperCase() === keyword) return null; // genuine keyword present
    if (!candidate && isKeywordTypo(t.text, keyword) && accept(idx)) {
      candidate = { token: t, keyword };
    }
  }
  return candidate;
}

function fromSlotAfterProjection(toks: readonly SyntaxToken[]): Candidate | null {
  return scanSlot(toks, 'FROM', (idx) => {
    if (idx < 2) return false; // non-empty projection before FROM
    const next = toks[idx + 1];
    return !!next && (next.type === 'word' || (next.type === 'punct' && next.text === '('));
  });
}

function setSlot(toks: readonly SyntaxToken[]): Candidate | null {
  return scanSlot(toks, 'SET', (idx) => {
    if (idx < 2) return false; // target before SET
    const col = toks[idx + 1];
    const eq = toks[idx + 2];
    return !!col && col.type === 'word' && !!eq && eq.type === 'op' && eq.text === '=';
  });
}

function valuesSlot(toks: readonly SyntaxToken[]): Candidate | null {
  return scanSlot(toks, 'VALUES', (idx) => {
    const next = toks[idx + 1];
    return !!next && next.type === 'punct' && next.text === '(';
  });
}

function deleteFromSlot(toks: readonly SyntaxToken[]): Candidate | null {
  const t = toks[1];
  if (!t || t.type !== 'word') return null;
  if (!isKeywordTypo(t.text, 'FROM')) return null;
  // Genuine FROM anywhere at depth 0 ⇒ slot filled (mysql multi-table DELETE).
  let depth = 0;
  for (const tok of toks) {
    if (tok.type === 'punct' && tok.text === '(') { depth += 1; continue; }
    if (tok.type === 'punct' && tok.text === ')') { depth -= 1; continue; }
    if (depth === 0 && tok.type === 'word' && tok.text.toUpperCase() === 'FROM') return null;
  }
  const rel = toks[2];
  return rel && rel.type === 'word' ? { token: t, keyword: 'FROM' } : null;
}

/** The typo word must sit ON, immediately before/after, or inside an ERROR span. */
function tokenNearError(
  token: SyntaxToken,
  code: string,
  statementStart: number,
  errorRanges: readonly WitnessRange[],
): boolean {
  for (const r of errorRanges) {
    const eStart = r.startOffset - statementStart;
    const eEnd = r.endOffset - statementStart;
    if (token.start < eEnd && eStart < token.end) return true; // overlap
    if (eStart <= 0 && eEnd >= code.length) return true; // E consumes statement
    if (token.end <= eStart && /^\s*$/.test(code.slice(token.end, eStart))) return true; // precedes
    if (eEnd <= token.start && /^\s*$/.test(code.slice(eEnd, token.start))) return true; // follows
  }
  return false;
}

// ── Structural contradiction ───────────────────────────────────────────────

function findStructuralWitness(
  toks: readonly SyntaxToken[],
  statementStart: number,
  explicitlyTerminated: boolean,
): SyntaxWitness | null {
  const candidates: SyntaxWitness[] = [];

  // Unbalanced parentheses.
  let depth = 0;
  const openStack: SyntaxToken[] = [];
  for (const t of toks) {
    if (t.type === 'punct' && t.text === '(') { depth += 1; openStack.push(t); continue; }
    if (t.type === 'punct' && t.text === ')') {
      if (depth === 0) {
        candidates.push(witnessAt(t, statementStart, "Unbalanced parentheses: unexpected ')'"));
      } else { depth -= 1; openStack.pop(); }
    }
  }
  if (explicitlyTerminated && openStack.length > 0) {
    const open = openStack[openStack.length - 1];
    candidates.push(witnessAt(open, statementStart, "Unbalanced parentheses: unclosed '('"));
  }

  // Infix operator / logical connective with no operand.
  for (let idx = 0; idx < toks.length; idx += 1) {
    const t = toks[idx];
    const isInfix = t.type === 'op' && INFIX_OPS.has(t.text);
    const isConnective = t.type === 'word' && CONNECTIVES.has(t.text.toUpperCase());
    if (!isInfix && !isConnective) continue;
    const next = toks[idx + 1];
    if (!next) {
      // Trailing operator/connective on an explicitly terminated statement.
      if (explicitlyTerminated) {
        candidates.push(witnessAt(t, statementStart, 'Incomplete expression: missing operand'));
      }
      continue;
    }
    // Oracle legacy outer-join marker `(+)` — never a dangling operator.
    if (t.text === '+' && isPunct(toks[idx - 1], '(') && isPunct(next, ')')) continue;
    const nextConnective = next.type === 'word' && CONNECTIVES.has(next.text.toUpperCase());
    const nextPunct = next.type === 'punct' && DANGLING_NEXT_PUNCT.has(next.text);
    if (nextConnective || nextPunct) {
      candidates.push(witnessAt(t, statementStart, 'Incomplete expression: missing operand'));
    }
  }

  if (candidates.length === 0) return null;
  candidates.sort((a, b) => a.start - b.start);
  return candidates[0];
}

function isPunct(tok: SyntaxToken | undefined, text: string): boolean {
  return !!tok && tok.type === 'punct' && tok.text === text;
}

function witnessAt(token: SyntaxToken, statementStart: number, message: string): SyntaxWitness {
  return { start: statementStart + token.start, end: statementStart + token.end, message };
}
